import { Component, lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import { Route, RouteComponentProps, Switch, withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { captureException } from 'lib/client/sentry/sentry';
import Header from 'components/Header/Header';
import Footer from 'components/Footer/Footer';
import NotFoundMessage from 'components/NotFoundMessage/NotFoundMessage';
import ScrollToTop from 'components/ScrollToTop/ScrollToTop';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import analytics from 'lib/client/analytics/analytics';
import { MastheadFooterType, MastheadHeaderType } from 'etc/types';
import { convertRelativeUrlToPlain, shouldRedirectToPlainPage } from 'context/PlainPage/plainPage';

import styles from './App.module.scss';

// Containers are chunked into separate JS files to make initial page load quicker
const CloseAccount = lazy(() => import(/* webpackChunkName: "CloseAccount" */ 'containers/CloseAccount/CloseAccount'));
const ContentLegalReporting = lazy(
  () =>
    import(
      /* webpackChunkName: "ContentLegalReporting" */ 'containers/ContentReporting/ContentLegalReporting/ContentLegalReporting'
    ),
);

const ContentPolicyReporting = lazy(
  () =>
    import(
      /* webpackChunkName: "ContentPolicyReporting" */ 'containers/ContentReporting/ContentPolicyReporting/ContentPolicyReporting'
    ),
);

interface AppProps extends RouteComponentProps {
  market: string;
}

interface AppState {
  headerData?: MastheadHeaderType;
  footerData?: MastheadFooterType;
}

class App extends Component<AppProps, AppState> {
  static propTypes = {
    market: PropTypes.string.isRequired,
  };

  constructor(props: AppProps) {
    super(props);

    this.state = {
      headerData: undefined,
      footerData: undefined,
    };
  }

  async componentDidMount() {
    this.initializeMasthead();
    analytics.initialize(this.props.market);
  }

  async initializeMasthead() {
    const MASTHEAD_API = 'https://www.spotify.com/api/masthead/v1/masthead';
    const queryParams = new URLSearchParams({
      market: this.props.market,
    });

    try {
      const response = await fetch(`${MASTHEAD_API}?${queryParams}`, {
        credentials: 'include',
      });

      if (response.ok) {
        const { header, footer } = await response.json();
        this.setState({
          headerData: header,
          footerData: footer,
        });
      } else {
        captureException(`Error when fetching masthead data, got: ${response.status}`);
      }
    } catch (e) {
      captureException(e);
    }
  }

  render() {
    const { headerData, footerData } = this.state;

    const currentPathname = this.props.location.pathname;
    if (shouldRedirectToPlainPage(document.referrer, currentPathname)) {
      this.props.history.replace({
        ...this.props.location,
        pathname: convertRelativeUrlToPlain(currentPathname),
      });
      return null;
    }

    return (
      <>
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>

        <ScrollToTop>
          <>
            <Header headerData={headerData} />
            <main className={styles.mainContainer}>
              <Suspense fallback={<LoadingSpinner />}>
                <Switch>
                  <Route path="/content-policy-reporting/" component={ContentPolicyReporting} />
                  <Route path="/close-account/" component={CloseAccount} />
                  <Route path="/report-content/" component={ContentLegalReporting} />
                  <Route component={NotFoundMessage} />
                </Switch>
              </Suspense>
            </main>
            <Footer footerData={footerData} />
          </>
        </ScrollToTop>
      </>
    );
  }
}

export default withRouter(App);
